import { useMutation, useQuery } from '@apollo/client';
import { ListAltOutlined } from '@mui/icons-material';
import { CircularProgress } from '@mui/material';
import { useGridApiRef } from '@mui/x-data-grid-pro';
import { DEFAULT_CURRENCY } from '@procureai/schemas';
import {
  CurrencyProvider,
  Widget,
  WidgetContent,
  WidgetHeader,
  WidgetIcon,
  WidgetEditActions,
  useCustomSnackbar,
} from '@procureai/ui-mui';
import Decimal from 'decimal.js';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { TsrctContractDemandRequestItemInput } from '@/generated/gql/graphql';
import { isContractDemandEditable } from '~/common/guards/contractDemand.guards';
import { I18nNamespace } from '~/common/types';
import { useOpenClose } from '~/hooks';
import { useDemandDetail } from '~/modules/Module.hooks';

import { ContractDemandRequestLineItems } from './components/ContractDemandRequestLineItems/ContractDemandRequestLineItems';
import { CONTRACT_DEMAND_REQUEST_MUTATION } from './ContractDemandRequestWidget.mutation.gql';
import { CONTRACT_DEMAND_REQUEST_QUERY } from './ContractDemandRequestWidget.query.gql';

export const ContractDemandRequestWidget = () => {
  const { demandId } = useDemandDetail();
  const { t } = useTranslation(I18nNamespace.widgets, { keyPrefix: 'ContractDemandRequestWidget' });

  const { onError, enqueueSuccessSnackbar } = useCustomSnackbar();

  const variables = {
    id: demandId,
  };

  const { data, loading } = useQuery(CONTRACT_DEMAND_REQUEST_QUERY, {
    variables,
    skip: !demandId,
  });
  const apiRef = useGridApiRef();
  const isEditable = isContractDemandEditable(data?.tesseract.contractDemand);
  const [update, { loading: saving }] = useMutation(CONTRACT_DEMAND_REQUEST_MUTATION, {
    refetchQueries: [{ query: CONTRACT_DEMAND_REQUEST_QUERY, variables }],
    onCompleted: () => enqueueSuccessSnackbar(t('saveSuccess')),
    onError,
  });
  const [{ open }, _, onToggle] = useOpenClose();

  const contractDemandRequest = data?.tesseract.contractDemand.demandRequest;
  const lineItems = useMemo(() => contractDemandRequest?.lineItems || [], [contractDemandRequest?.lineItems]);
  const onSave = useCallback(() => {
    const rows = Array.from(apiRef?.current.getRowModels?.().values()).map(
      (row): TsrctContractDemandRequestItemInput => {
        return {
          id: row.id,
          quantity: row.quantity?.toString(),
          organizationId: row.organizationId,
          unitId: row.unitId,
          priceUnit: row.priceUnit?.toString(),
          title: row.title,
          baselinePricePerUnit: new Decimal(row.baselinePricePerPriceUnit).div(row.priceUnit).toString(),
          commodityGroupId: row.commodityGroupId,
          commodityId: row.commodityId,
          description: row.description,
          minimumOrderQuantity: row.minimumOrderQuantity?.toString(),
          deliveryLocationId: null,
          qualifiedSuppliers: row.qualifiedSuppliers,
        };
      },
    );
    update({
      variables: {
        demandId,
        data: {
          lineItems: rows,
        },
      },
    });
    onToggle();
  }, [apiRef, update, demandId, onToggle]);

  const onCancel = useCallback(() => {
    apiRef.current.setRows(lineItems);
  }, [apiRef, lineItems]);

  return (
    <Widget hover={false} data-intercom-target="DemandRequestWidget">
      <WidgetHeader
        title={t('title')}
        icon={<WidgetIcon icon={<ListAltOutlined fontSize="small" />} />}
        actionsLeft={loading || saving ? <CircularProgress size={20} /> : undefined}
        actionsRight={
          <WidgetEditActions
            onSave={onSave}
            open={open}
            saving={saving}
            disabled={!isEditable}
            onToggle={onToggle}
            onCancel={onCancel}
          />
        }
      />
      <WidgetContent>
        <CurrencyProvider value={contractDemandRequest?.currency ?? DEFAULT_CURRENCY}>
          <ContractDemandRequestLineItems viewOnly={!open} lineItems={lineItems} apiRef={apiRef} loading={loading} />
        </CurrencyProvider>
      </WidgetContent>
    </Widget>
  );
};
